import { clsxm } from '@ui/utils/style.util';
import { ComponentProps, createElement, memo, type JSX } from 'react';

// #region Types
export type TypographyProps<Type extends keyof JSX.IntrinsicElements = 'p'> =
  ComponentProps<Type> & { as?: Type };
// #endregion

// #region Components
/**
 * HeadingOne - Largest heading component
 * Used for main titles and hero sections
 */
function HeadingOne<Type extends keyof JSX.IntrinsicElements = 'h1'>({
  as = 'h1' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-9xl font-bold', className), ...props },
    children,
  );
}

/**
 * HeadingTwo - Second level heading
 * Used for section titles and major content divisions
 */
function HeadingTwo<Type extends keyof JSX.IntrinsicElements = 'h2'>({
  as = 'h2' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-3xl font-bold', className), ...props },
    children,
  );
}

/**
 * HeadingThree - Third level heading
 * Used for subsection titles
 */
function HeadingThree<Type extends keyof JSX.IntrinsicElements = 'h3'>({
  as = 'h3' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-2xl font-bold leading-8', className), ...props },
    children,
  );
}

/**
 * HeadingFour - Fourth level heading
 * Used for minor section titles
 */
function HeadingFour<Type extends keyof JSX.IntrinsicElements = 'h4'>({
  as = 'h4' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-xl font-bold leading-8', className), ...props },
    children,
  );
}

/**
 * HeadingFive - Fifth level heading
 * Used for small section titles
 */
function HeadingFive<Type extends keyof JSX.IntrinsicElements = 'h5'>({
  as = 'h5' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(as, { className: clsxm('font-medium', className), ...props }, children);
}

/**
 * HeadingSix - Smallest heading
 * Used for the smallest section titles
 */
function HeadingSix<Type extends keyof JSX.IntrinsicElements = 'h6'>({
  as = 'h6' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-sm font-medium leading-6', className), ...props },
    children,
  );
}

/**
 * BodyOne - Primary body text
 * Used for main content and paragraphs
 */
function BodyOne<Type extends keyof JSX.IntrinsicElements = 'p'>({
  as = 'p' as Type,
  children,
  ...props
}: TypographyProps<Type>) {
  return createElement(as, props, children);
}

/**
 * BodyTwo - Secondary body text
 * Used for supporting content and descriptions
 */
function BodyTwo<Type extends keyof JSX.IntrinsicElements = 'p'>({
  as = 'p' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-sm leading-6', className), ...props },
    children,
  );
}

/**
 * BodyThree - Smallest body text
 * Used for captions, labels and helper text
 */
function BodyThree<Type extends keyof JSX.IntrinsicElements = 'p'>({
  as = 'p' as Type,
  children,
  className,
  ...props
}: TypographyProps<Type>) {
  return createElement(
    as,
    { className: clsxm('text-xs leading-5', className), ...props },
    children,
  );
}
// #endregion

// Export all typography components as a single object
const Typography = {
  BodyOne: memo(BodyOne) as typeof BodyOne,
  BodyThree: memo(BodyThree) as typeof BodyThree,
  BodyTwo: memo(BodyTwo) as typeof BodyTwo,
  HeadingFive: memo(HeadingFive) as typeof HeadingFive,
  HeadingFour: memo(HeadingFour) as typeof HeadingFour,
  HeadingOne: memo(HeadingOne) as typeof HeadingOne,
  HeadingSix: memo(HeadingSix) as typeof HeadingSix,
  HeadingThree: memo(HeadingThree) as typeof HeadingThree,
  HeadingTwo: memo(HeadingTwo) as typeof HeadingTwo,
};

export default Typography;
